import type { ListingAgentPhoneType } from "@/api/buyers";
import type { AgentForm } from "../../ListingAgentDirectory";
import type { AgentPasteChoice } from "./AgentPasteToFill";
import {
  applyPastedValue,
  changedFields,
  mergePasteIntoAgentForm,
  PASTE_MARKER_TEXT,
  type PasteConflict,
  type PasteField,
  type PasteMarker,
} from "./mergePaste";

/**
 * What the New listing agent form remembers about a paste between renders:
 * which fields carry a "from paste" marker, the conflicts still to decide, the
 * line offered for Notes, and how many values the server left out. Pure
 * transitions, so the form's behaviour is pinned without a render.
 */
export interface PasteFormState {
  marks: Partial<Record<PasteField, PasteMarker>>;
  conflicts: PasteConflict[];
  notesOffer: string | null;
  droppedValueCount: number;
  /** The pasted phone type — "Use pasted" on a phone conflict brings it along. */
  pastedPhoneType: ListingAgentPhoneType;
  marker: PasteMarker;
}

export const NO_PASTE: PasteFormState = {
  marks: {},
  conflicts: [],
  notesOffer: null,
  droppedValueCount: 0,
  pastedPhoneType: "Unknown",
  marker: "paste",
};

/** A chosen person merged into the form: blanks filled and marked, conflicts
 *  listed, the notes line offered. Markers from an earlier paste stay on the
 *  fields nobody has edited since. */
export function pasteIntoForm(
  form: AgentForm,
  choice: AgentPasteChoice,
  prior: PasteFormState,
): { form: AgentForm; paste: PasteFormState } {
  const merged = mergePasteIntoAgentForm(form, choice.candidate, {
    fromImage: choice.fromImage,
    notesLine: choice.notesLine,
  });
  const marks = { ...prior.marks };
  merged.filled.forEach((field) => {
    marks[field] = merged.marker;
  });
  return {
    form: merged.form,
    paste: {
      marks,
      conflicts: merged.conflicts,
      notesOffer: merged.notesLine,
      droppedValueCount: choice.droppedValueCount,
      pastedPhoneType: choice.candidate.phoneType ?? "Unknown",
      marker: merged.marker,
    },
  };
}

/** The specialist edited the form: every field they changed loses its marker,
 *  and a conflict on it is settled by what they typed. */
export function pasteAfterEdit(before: AgentForm, after: AgentForm, paste: PasteFormState): PasteFormState {
  const changed = changedFields(before, after);
  if (changed.length === 0) return paste;
  const marks = { ...paste.marks };
  changed.forEach((field) => delete marks[field]);
  return {
    ...paste,
    marks,
    conflicts: paste.conflicts.filter((c) => !changed.includes(c.field)),
  };
}

/** "Use pasted": the field takes the pasted value and carries the marker. */
export function choosePasted(
  form: AgentForm,
  conflict: PasteConflict,
  paste: PasteFormState,
): { form: AgentForm; paste: PasteFormState } {
  const applied = applyPastedValue(form, conflict, paste.pastedPhoneType);
  const marks = { ...paste.marks };
  applied.filled.forEach((field) => {
    marks[field] = paste.marker;
  });
  return {
    form: applied.form,
    paste: { ...paste, marks, conflicts: paste.conflicts.filter((c) => c !== conflict) },
  };
}

/** "Keep typed": the conflict goes; the form is unchanged. */
export function keepTyped(conflict: PasteConflict, paste: PasteFormState): PasteFormState {
  return { ...paste, conflicts: paste.conflicts.filter((c) => c !== conflict) };
}

/** The marker sentence per field, as `AgentFieldsBlock` renders it. */
export function markerTexts(paste: PasteFormState): Partial<Record<PasteField, string>> {
  const texts: Partial<Record<PasteField, string>> = {};
  (Object.keys(paste.marks) as PasteField[]).forEach((field) => {
    const marker = paste.marks[field];
    if (marker) texts[field] = PASTE_MARKER_TEXT[marker];
  });
  return texts;
}
